import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { PatientBehaviorDataModel, PatientTargetDataModel } from '../api/models';
import { StaffApiService } from '../api/services';
import { Utils } from '../utils';

@Injectable({ providedIn: 'root' })
export class PatientDataEditService {
  constructor(private staffApiService: StaffApiService) {}

  save<T extends PatientTargetDataModel | PatientBehaviorDataModel>(
    entry: T,
    original: T | undefined,
    saveValues: (submitted: T) => Observable<T>
  ): Observable<T> {
    const submitted: T = Utils.deepClone(entry);
    if (!original || submitted.RecordedDate === original.RecordedDate) {
      return saveValues(submitted);
    }
    if (!submitted.ID || !submitted.RecordedDate || !original.RecordedDate) {
      return throwError(() => new Error('Select a saved entry and enter a valid recorded date.'));
    }

    const auditFields = ['RecordedDate', 'ModifiedDate', 'ModifiedByUserID', 'ModifiedByUser', 'UserID', 'UserFullName'];
    const valuesChanged = Object.keys(submitted).some((field) =>
      !auditFields.includes(field) &&
      JSON.stringify(submitted[field as keyof T]) !== JSON.stringify(original[field as keyof T])
    );
    return this.staffApiService.UpdatePatientDataRecordedDate({
      dataID: submitted.ID,
      isBehaviorData: 'PatientBehaviorID' in submitted,
      recordedDate: submitted.RecordedDate,
      originalRecordedDate: original.RecordedDate,
      originalModifiedDate: original.ModifiedDate ?? undefined,
    }).pipe(
      switchMap((result) => {
        // Keep the successful date correction if a subsequent value save fails.
        Object.assign(original, result);
        const datedEntry = { ...submitted, ...result };
        if (!valuesChanged) return of(datedEntry);
        return saveValues(datedEntry).pipe(
          catchError((error) => throwError(() => new Error(
            'The recorded date was saved, but the value changes could not be saved. ' + Utils.getAjaxError(error)
          )))
        );
      })
    );
  }
}
